import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressMembersCustomfieldsSuggestionsCustomFieldSuggestionsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/members-custom-fields-suggestions-api',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/members/v1/custom-field-suggestions',
        destPath: '/v1/custom-field-suggestions',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/members/v1/custom-field-suggestions',
        destPath: '/v1/custom-field-suggestions',
      },
    ],
    'editor.wixapps.net': [
      {
        srcPath: '/_api/members/v1/custom-field-suggestions',
        destPath: '/v1/custom-field-suggestions',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/members/v1/custom-field-suggestions',
        destPath: '/v1/custom-field-suggestions',
      },
    ],
    _: [
      {
        srcPath: '/members/v1/custom-field-suggestions',
        destPath: '/v1/custom-field-suggestions',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_members_custom-field-suggestions';

/** Retrieves a list of suggestions for both custom fields and existing fields available from the Wix Members Area. */
export function listCustomFieldSuggestions(
  payload: object
): RequestOptionsFactory<any> {
  function __listCustomFieldSuggestions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'sorting.origin.latitude' },
          { path: 'sorting.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_suggestion',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.members.customfields.suggestions.CustomFieldSuggestions.ListCustomFieldSuggestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsSuggestionsCustomFieldSuggestionsUrl(
        {
          protoPath: '/v1/custom-field-suggestions',
          data: serializedData,
          host,
        }
      ),
      params: toURLSearchParams(serializedData, true),
    };

    return metadata;
  }

  return __listCustomFieldSuggestions;
}

/**
 * Retrieves a list of up to 100 custom field suggestions, given the provided filters, fieldsets, sorting and paging, and returns a [`SuggestionsQueryBuilder`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/find) object.
 *
 * The returned object contains the query definition which is typically used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/find) method.
 *
 * You can refine the query by chaining `SuggestionsQueryBuilder` methods onto the query. `SuggestionsQueryBuilder` methods enable you to sort, filter, and control the results that `queryCustomFieldSuggestions()` returns. The methods that are chained to `queryCustomFieldSuggestions()` are applied in the order they are called.
 *
 * `queryCustomFieldSuggestions()` runs with the following `SuggestionsQueryBuilder` defaults that you can override:
 * - [`skip`: `0`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/skip)
 * - [`limit`: `50`](https://dev.wix.com/docs/sdk/backend-modules/members/custom-field-suggestions/suggestions-query-builder/limit)
 *
 *
 */
export function queryCustomFieldSuggestions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryCustomFieldSuggestions({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'query.sorting.origin.latitude' },
          { path: 'query.sorting.origin.longitude' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.members.v1.custom_field_suggestion',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.members.customfields.suggestions.CustomFieldSuggestions.QueryCustomFieldSuggestions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressMembersCustomfieldsSuggestionsCustomFieldSuggestionsUrl(
        {
          protoPath: '/v1/custom-field-suggestions/query',
          data: serializedData,
          host,
        }
      ),
      data: serializedData,
    };

    return metadata;
  }

  return __queryCustomFieldSuggestions;
}
